import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Holds LaunchDarkly flag values that something outside a component `setup()` has to read —
 * the router, middleware, or a store. Flags consumed inside a single component call
 * `useLDFlag()` directly and do not belong here.
 *
 * Synced from `App.vue` with `watch(..., { immediate: true })`, because flags resolve
 * asynchronously and cannot be read synchronously during route evaluation.
 *
 * Context: this is a flag store rather than a field on each domain store (PE-614, DL-44). The
 * precedent — `unifiedAuthEnabled` — lives on the store the flag governs, which works when the
 * flag *is* about auth. A feature flag that gates
 * a whole surface belongs to no single domain, and its consumers (the router, a tab, a create
 * affordance) span several.
 */
export const useFeatureFlagsStore = defineStore('feature-flags', () => {
  /**
   * `erp_app_bulk_orders_v1` — gates every Bulk Orders surface.
   *
   * Defaults to off, and off is also what an unresolved flag means: LaunchDarkly resolves
   * after mount, so a guard reading this on page load sees `false` until the sync fires.
   * Treating unresolved as on would let a navigation through in that window.
   */
  const bulkOrdersEnabled = ref(false);

  const setBulkOrdersEnabled = (value: boolean): void => {
    bulkOrdersEnabled.value = value;
  };

  /**
   * `erp_app_proof_of_payment_ingestion_v1`: gates the payments surface, the payment record and the
   * unmatched-proofs tab (the frontend half of the pair; the API's is `core_api_...`).
   *
   * Off, like an unresolved flag, is today's behaviour. A route guard reading this on page load
   * sees `false` until the sync fires, so the payments routes wait for the permission and role
   * flags to resolve before deciding (PE-1004, the Bulk Orders tab precedent).
   */
  const paymentLifecycleEnabled = ref(false);

  const setPaymentLifecycleEnabled = (value: boolean): void => {
    paymentLifecycleEnabled.value = value;
  };

  /**
   * `erp_app_activity_logs_vendors_sites_projects_v1`: whether the vendor, site and project
   * drawers (and the other mounts that follow them) show an Activity Log.
   *
   * Context: one flag covers the whole feature so the mounts ship together and clean-up removes a
   * single ref. `App.vue` is its only reader; a mount reads this store, never `useLDFlag`.
   */
  const activityLogsVendorsSitesProjectsEnabled = ref(false);

  const setActivityLogsVendorsSitesProjectsEnabled = (value: boolean): void => {
    activityLogsVendorsSitesProjectsEnabled.value = value;
  };

  /**
   * `erp_app_role_capability_overview_v1`: the Role capabilities page and its account-menu entry.
   *
   * Off, like an unresolved flag: the menu entry stays hidden until the flag resolves on. The route
   * waits for `permissionStore.areRoleFlagsResolved` before refusing, so a reload isn't bounced
   * while the flag is still loading.
   */
  const roleCapabilityOverviewEnabled = ref(false);

  const setRoleCapabilityOverviewEnabled = (value: boolean): void => {
    roleCapabilityOverviewEnabled.value = value;
  };

  /**
   * `erp_app_provisional_budgets_v1`: whether provisional budgets can be created and decided, and
   * whether their entry points show on a bill that has none yet.
   *
   * Context: off is a write switch, never a visibility switch (HL v9 §13). A bill that already has a
   * budget keeps it readable, because that reserve is already in the BOQ total and must stay
   * explained. Off, like an unresolved flag; the slice trusts it once `areRoleFlagsResolved` is true.
   */
  const provisionalBudgetsEnabled = ref(false);

  const setProvisionalBudgetsEnabled = (value: boolean): void => {
    provisionalBudgetsEnabled.value = value;
  };

  /**
   * `erp_app_boq_catalog_admin_v1`: the BOQ catalog admin's landing on Vendors, their view-only
   * settings menu, and the BOQ database button keyed on MANAGE:BOQ_VENDOR rather than on platform
   * staff. Off, like an unresolved flag, is today's behaviour for every user.
   */
  const boqCatalogAdminEnabled = ref(false);

  const setBoqCatalogAdminEnabled = (value: boolean): void => {
    boqCatalogAdminEnabled.value = value;
  };

  /**
   * `erp_app_boq_database_vendors_rework_v1`: the BOQ database pages on the side-nav shell and
   * `BaseLayout`, with the database banner in their header. Off, like an unresolved flag, keeps
   * `BOQAdminLayout`.
   *
   * Context: separate from `boqCatalogAdminEnabled` because the restyle reaches BOQ_ADMIN as well,
   * so it rolls out on its own rather than with the new role (PLE-1660).
   */
  const boqDatabaseVendorsReworkEnabled = ref(false);

  const setBoqDatabaseVendorsReworkEnabled = (value: boolean): void => {
    boqDatabaseVendorsReworkEnabled.value = value;
  };

  /**
   * `erp_app_boq_layout_foundation_v1`: the BOQ tab's new frame (header, summary strip, filter
   * bar). Off, like an unresolved flag, is today's frame exactly. Frame code reads it only
   * through `useBoqFrameEnabled()`; `App.vue` reads it directly to pick the layout.
   *
   * Context: the switch picks the page layout, so a value change after first render remounts the
   * BOQ and refetches the project, site and bill. It stays one global switch, never targeted per
   * org or per user mid-session (FR-A5.1).
   */
  const boqLayoutFoundationEnabled = ref(false);

  const setBoqLayoutFoundationEnabled = (value: boolean): void => {
    boqLayoutFoundationEnabled.value = value;
  };

  return {
    bulkOrdersEnabled,
    setBulkOrdersEnabled,
    paymentLifecycleEnabled,
    setPaymentLifecycleEnabled,
    activityLogsVendorsSitesProjectsEnabled,
    setActivityLogsVendorsSitesProjectsEnabled,
    roleCapabilityOverviewEnabled,
    setRoleCapabilityOverviewEnabled,
    provisionalBudgetsEnabled,
    setProvisionalBudgetsEnabled,
    boqCatalogAdminEnabled,
    setBoqCatalogAdminEnabled,
    boqDatabaseVendorsReworkEnabled,
    setBoqDatabaseVendorsReworkEnabled,
    boqLayoutFoundationEnabled,
    setBoqLayoutFoundationEnabled,
  };
});
